'use client';

import { useEffect, useMemo, useRef } from 'react';
import { toast } from 'sonner';

import { useOrganizationFeature } from '@/hooks/useOrganizationFeature';
import { useTranscriptionRecordingSession } from '@/hooks/useTranscriptionRecordingSession';
import { logger } from '@/lib/logger';

import type { TranscriptionMinutesStatus } from '@/types/transcription';

const MAX_ATTEMPTED_AUTOGEN_MEETING_IDS = 256;

type FormatMinutesResponse = {
  code?: string;
  success?: boolean;
  error?: string;
  data?: {
    minutes?: string;
  };
};

type StoredMinutesResponse = {
  success?: boolean;
  error?: string;
  data?: {
    minutes?: string | null;
    minutes_status?: TranscriptionMinutesStatus;
    saved?: boolean;
  };
};

type UpdateMinutesStatusResponse = {
  success?: boolean;
  error?: string;
  data?: {
    transcriptionId?: string;
    minutes_status?: TranscriptionMinutesStatus;
  };
};

function normalizeMinutesStatus(value: unknown): TranscriptionMinutesStatus {
  if (value === 'processing' || value === 'completed' || value === 'failed') {
    return value;
  }
  return 'none';
}

async function fetchStoredMinutes(
  transcriptionId: string
): Promise<{ minutes: string | null; status: TranscriptionMinutesStatus } | null> {
  try {
    const response = await fetch(`/api/transcriptions/${encodeURIComponent(transcriptionId)}/minutes`, {
      method: 'GET',
      cache: 'no-store',
    });

    const payload = (await response.json().catch(() => null)) as StoredMinutesResponse | null;
    if (!response.ok || !payload?.success) {
      return null;
    }

    const minutes = payload.data?.minutes;
    const status = normalizeMinutesStatus(payload.data?.minutes_status);
    const normalizedMinutes =
      typeof minutes === 'string' && minutes.trim().length > 0 ? minutes.trim() : null;
    return { minutes: normalizedMinutes, status: normalizedMinutes ? 'completed' : status };
  } catch {
    return null;
  }
}

async function requestMinutesFormat(text: string): Promise<string | null> {
  const response = await fetch('/api/transcriptions/format-minutes', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ text, detailLevel: 'high' }),
  });

  const payload = (await response.json().catch(() => null)) as FormatMinutesResponse | null;
  if (response.status === 403 && payload?.code === 'ORGANIZATION_FEATURE_DISABLED') {
    return null;
  }
  if (!response.ok || !payload?.success) {
    throw new Error(payload?.error || '議事録の整形に失敗しました');
  }

  const minutes = payload.data?.minutes?.trim();
  if (!minutes) {
    throw new Error('議事録が生成されませんでした');
  }
  return minutes;
}

async function updateMinutesStatus(
  transcriptionId: string,
  status: TranscriptionMinutesStatus,
): Promise<void> {
  const response = await fetch(
    `/api/transcriptions/${encodeURIComponent(transcriptionId)}/minutes-status`,
    {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ status }),
    },
  );

  const payload = (await response.json().catch(() => null)) as UpdateMinutesStatusResponse | null;
  if (!response.ok || !payload?.success) {
    throw new Error(payload?.error || '議事録サマリのステータス更新に失敗しました');
  }
}

async function saveMinutes(transcriptionId: string, minutes: string): Promise<void> {
  const response = await fetch(`/api/transcriptions/${encodeURIComponent(transcriptionId)}/minutes`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ minutes }),
  });

  const payload = (await response.json().catch(() => null)) as StoredMinutesResponse | null;
  if (!response.ok || !payload?.success) {
    throw new Error(payload?.error || '議事録の保存に失敗しました');
  }
}

async function tryUpdateMinutesStatus(meetingId: string, status: TranscriptionMinutesStatus): Promise<void> {
  try {
    await updateMinutesStatus(meetingId, status);
  } catch (error) {
    logger.warn('[transcription] failed to update minutes status', { meetingId, status, error });
  }
}

async function autoGenerateMinutes(meetingId: string, transcript: string, attempted: Set<string>): Promise<void> {
  try {
    const existing = await fetchStoredMinutes(meetingId);
    if (existing?.minutes || existing?.status === 'processing' || existing?.status === 'completed') return;
    if (transcript.length > 200_000) {
      toast.error('文字起こしが長すぎるため、議事録サマリの自動作成をスキップしました');
      return;
    }
    await tryUpdateMinutesStatus(meetingId, 'processing');
    const minutes = await requestMinutesFormat(transcript);
    if (minutes === null) {
      // 設定変更による拒否は生成失敗ではない。processing を元の状態へ戻す。
      await tryUpdateMinutesStatus(meetingId, existing?.status ?? 'none');
      attempted.delete(meetingId);
      return;
    }
    await saveMinutes(meetingId, minutes);
    logger.info('[transcription] minutes auto-generated after recording', { meetingId });
  } catch (error) {
    logger.warn('[transcription] failed to auto-generate minutes after recording', { meetingId, error });
    await tryUpdateMinutesStatus(meetingId, 'failed');
    toast.error('議事録サマリの自動作成に失敗しました');
  }
}

/**
 * 録音セッションが完了し、整形済み文字起こしが確定した後に、
 * 議事録サマリ（minutes）をバックグラウンドで自動作成する。
 */
export function TranscriptionMinutesAutogen() {
  const session = useTranscriptionRecordingSession();
  const transcriptionEnabled = useOrganizationFeature('transcriptionEnabled');

  const meetingId = useMemo(() => {
    const raw = session.meetingId;
    if (typeof raw !== 'string') {
      return null;
    }
    const trimmed = raw.trim();
    return trimmed.length > 0 ? trimmed : null;
  }, [session.meetingId]);

  const formattedTranscript = useMemo(() => session.formattedTranscript.trim(), [session.formattedTranscript]);
  const readyToAutogen = transcriptionEnabled && session.engineState === 'completed' && Boolean(meetingId) && formattedTranscript.length > 0;

  const attemptedMeetingIdsRef = useRef<Set<string>>(new Set());

  useEffect(() => {
    if (!readyToAutogen || !meetingId) {
      return;
    }
    if (attemptedMeetingIdsRef.current.has(meetingId)) {
      return;
    }

    // Wait a short quiet period so we don't generate from a mid-stream formatted snapshot.
    const timerId = window.setTimeout(() => {
      if (attemptedMeetingIdsRef.current.has(meetingId)) {
        return;
      }
      if (attemptedMeetingIdsRef.current.size >= MAX_ATTEMPTED_AUTOGEN_MEETING_IDS) {
        attemptedMeetingIdsRef.current.clear();
      }
      attemptedMeetingIdsRef.current.add(meetingId);

      void autoGenerateMinutes(meetingId, formattedTranscript, attemptedMeetingIdsRef.current);
    }, 3000);

    return () => {
      window.clearTimeout(timerId);
    };
  }, [formattedTranscript, meetingId, readyToAutogen]);

  return null;
}
